import {
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Typography,
    Divider,
    InputAdornment,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
  } from "@material-ui/core";
  import { Info, ChevronLeft } from "@material-ui/icons";
  import { makeStyles } from "@material-ui/core/styles";
  import React, { useState, useEffect } from "react";
  import { useDispatch } from "react-redux";
  import axios from "axios";
  
  import { PDFViewer } from "../sasl/batch/updateBatch/StructuredInformation/PDFViewer";
  import { fetchDSBatchDetails } from "../../../containers/actions/common/saslAction";
  import { appUrl } from "../../../../../utils/config";

  const useStyles = makeStyles((theme) => ({
    formLabel: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      minWidth: "150px",
      paddingRight: theme.spacing(2),
      textAlign: "right",
    },
    formField: {
      marginBottom: theme.spacing(2),
    },
    requiredStar: {
      color: "red",
      marginLeft: 4,
    },
  }));

function CommercialInvoice(props) {
    const { initialData, onClose, schrockenCustomFileID, digitizedData, batchDetails, poNumber, batchNumber, onDataRefresh } = props;
    const dispatch = useDispatch();
    const [isEditable, setIsEditable] = useState(false);
    const [formValues, setFormValues] = useState(initialData || {});
    const [originalValues, setOriginalValues] = useState(initialData || {});
    const [lastSavedValues, setLastSavedValues] = useState(initialData || {});
    const [isShowingOriginal, setIsShowingOriginal] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [validationCounts, setValidationCounts] = useState({
      noOfFieldsToBeReviewed: 0,
      noOfFieldsReviewed: 0,
    });
    const [modalData, setModalData] = useState({
      field: "",
      before: "",
      after: "",
      updatedBy: "user123",
      timestamp: new Date().toLocaleString(),
    });

    const classes = useStyles();

    useEffect(() => {
      const dataToUse = initialData || {};
      setFormValues(dataToUse);
      setOriginalValues(dataToUse);
      setLastSavedValues(dataToUse);
      
      // Initialize validation counts
      const noOfFieldsToBeReviewed = fields.reduce((count, field) => {
        const fieldData = dataToUse[field.key];
        if (fieldData && typeof fieldData === 'object' && fieldData.needValidation === true) {
          return count + 1;
        }
        return count;
      }, 0);
      
      setValidationCounts({
        noOfFieldsToBeReviewed,
        noOfFieldsReviewed: dataToUse.noOfFieldsReviewed || 0,
      });
    }, [initialData]);

    // Helper function to get data from commercialInvoiceJSONEdited or digitizedExtractedData
    const getCommercialInvoiceFromExtractedData = () => {
      if (batchDetails?.digitizedExtractedData) {
        const commercialInvoiceDoc = batchDetails.digitizedExtractedData.find(doc => doc.documentType === "CommercialInvoice");
        return commercialInvoiceDoc?.CommericalInvoiceJSONOriginal || null;
      }
      return null;
    };

    // Helper function to check if commercialInvoiceJSONEdited has data
    const hasCommercialInvoiceJSONEditedData = () => {
      return batchDetails?.commercialInvoiceJSONEdited && Object.keys(batchDetails.commercialInvoiceJSONEdited).length > 0;
    };

    const getConfidenceForField = (fieldKey) => {
      const fieldData = formValues[fieldKey];
      if (fieldData && typeof fieldData === 'object' && fieldData.confidence) {
        return parseFloat(fieldData.confidence);
      }
      
      // Default confidence values based on the original mock data
      const defaultConfidences = {
        invoice_number: 95,
        invoice_date: 95,
        product_code: 54,
        product_description: 54,
        quantity: 93
      };
      return defaultConfidences[fieldKey] || 90;
    };

    // Field mappings based on the API response structure
    const fields = [
      { key: "invoice_number", label: "CMO Invoice #" },
      { key: "invoice_date", label: "CMO Invoice Date" },
      { key: "product_code", label: "Sponsor SKU" },
      { key: "product_description", label: "Sponsor Description" },
      { key: "quantity", label: "Actual Component Quantity Consumed" },
    ];

    // Validation functions
    const validateField = (fieldKey, value) => {
      const validations = {
        invoice_number: {
          validate: (val) => {
            if (!val || val.trim() === '') return "CMO Invoice # is required";
            if (!/^[a-zA-Z0-9\s]*$/.test(val)) return "Must contain only alphanumeric characters";
            return null;
          }
        },
        invoice_date: {
          validate: (val) => {
            if (!val || val.trim() === '') return "CMO Invoice Date is required";
            return null;
          }
        },
        product_code: {
          validate: (val) => {
            if (!val || val.trim() === '') return null; // Optional field
            if (!/^\d*$/.test(val)) return "Must be numeric";
            return null;
          }
        },
        product_description: {
          validate: (val) => {
            if (!val || val.trim() === '') return null; // Optional field
            return null;
          }
        },
        quantity: {
          validate: (val) => {
            if (!val || val.trim() === '') return null; // Optional field
            if (!/^\d*\.?\d*$/.test(val)) return "Must be a valid number";
            return null;
          }
        }
      };

      const fieldValidation = validations[fieldKey];
      return fieldValidation ? fieldValidation.validate(value) : null;
    };

    const validateAllFields = () => {
      const errors = {};
      fields.forEach(field => {
        const error = validateField(field.key, getValueForField(field.key));
        if (error) {
          errors[field.key] = error;
        }
      });

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const getValueForField = (fieldKey) => {
      const fieldData = formValues[fieldKey];
      if (fieldData && typeof fieldData === 'object' && fieldData.value) {
        return fieldData.value;
      }
      return fieldData || '';
    };

    const getLevelColor = (level) => {
        const num = typeof level === "string" ? parseFloat(level) : level;
        if (num < 80) return "red";
        if (num <= 90) return "orange";
        return "green";
      };
    
      const handleInputChange = (key, value) => {
        let isValid = true;

    // Inline filtering logic
    switch (key) {
     
      case "product_code":
        isValid = /^[0-9]*$/.test(value);
        break;
      case "invoice_number":
        isValid = /^[a-zA-Z0-9\s]*$/.test(value);
        break;
        case "quantity":
          isValid = /^\d*\.?\d*$/.test(value);
          break;
          case "product_description":
        // Allow alphanumeric, spaces, and common symbols
        isValid = /^[a-zA-Z0-9\s().-]*$/.test(value);
        break;
        case "invoice_date":
          // Allow date formats like 05/23/2025 or similar
          isValid = /^(\d{0,2})?\/?(0?[1-9]|1[0-2])?\/?(\d{0,4})?$/.test(value) || /^(\d{0,2})?-?(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)?-?(\d{0,4})?$/i.test(value);
          break;

      default:
        isValid = true;
    }

    if (!isValid) return;
        
        const newValues = {
          ...formValues,
          [key]: typeof formValues[key] === 'object' 
            ? { ...formValues[key], value: value }
            : value,
        };
        setFormValues(newValues);
        if (!isShowingOriginal) {
          setLastSavedValues(newValues);
        }

        // Validate the field and update errors
        const error = validateField(key, value);
        setValidationErrors(prev => ({
          ...prev,
          [key]: error
        }));
      };

      const handleSave = async () => {
        if (validateAllFields()) {
          try {
            setSaving(true);
            
            // Get current Commercial Invoice data
            const currentCommercialInvoiceData = hasCommercialInvoiceJSONEditedData() 
              ? batchDetails.commercialInvoiceJSONEdited 
              : (getCommercialInvoiceFromExtractedData() || {});

            // Build updated JSON structure with all fields
            const updatedCommercialInvoiceJSON = {
              ...currentCommercialInvoiceData,
              invoice_number: {
                value: getValueForField('invoice_number'),
                confidence: getConfidenceForField('invoice_number'),
                needValidation: currentCommercialInvoiceData.invoice_number?.needValidation ?? false,
                ...(currentCommercialInvoiceData.invoice_number?.reasonCode && { reasonCode: currentCommercialInvoiceData.invoice_number.reasonCode }),
                ...(currentCommercialInvoiceData.invoice_number?.comment && { comment: currentCommercialInvoiceData.invoice_number.comment })
              },
              invoice_date: {
                value: getValueForField('invoice_date'),
                confidence: getConfidenceForField('invoice_date'),
                needValidation: currentCommercialInvoiceData.invoice_date?.needValidation ?? false,
                ...(currentCommercialInvoiceData.invoice_date?.reasonCode && { reasonCode: currentCommercialInvoiceData.invoice_date.reasonCode }),
                ...(currentCommercialInvoiceData.invoice_date?.comment && { comment: currentCommercialInvoiceData.invoice_date.comment })
              },
              product_code: {
                value: getValueForField('product_code'),
                confidence: getConfidenceForField('product_code'),
                needValidation: currentCommercialInvoiceData.product_code?.needValidation ?? false,
                ...(currentCommercialInvoiceData.product_code?.reasonCode && { reasonCode: currentCommercialInvoiceData.product_code.reasonCode }),
                ...(currentCommercialInvoiceData.product_code?.comment && { comment: currentCommercialInvoiceData.product_code.comment })
              },
              product_description: {
                value: getValueForField('product_description'),
                confidence: getConfidenceForField('product_description'),
                needValidation: currentCommercialInvoiceData.product_description?.needValidation ?? false,
                ...(currentCommercialInvoiceData.product_description?.reasonCode && { reasonCode: currentCommercialInvoiceData.product_description.reasonCode }),
                ...(currentCommercialInvoiceData.product_description?.comment && { comment: currentCommercialInvoiceData.product_description.comment })
              },
              quantity: {
                value: getValueForField('quantity'),
                confidence: getConfidenceForField('quantity'),
                needValidation: currentCommercialInvoiceData.quantity?.needValidation ?? false,
                ...(currentCommercialInvoiceData.quantity?.reasonCode && { reasonCode: currentCommercialInvoiceData.quantity.reasonCode }),
                ...(currentCommercialInvoiceData.quantity?.comment && { comment: currentCommercialInvoiceData.quantity.comment })
              },
              // Keep existing metadata fields
              batch: currentCommercialInvoiceData.batch,
              uom: currentCommercialInvoiceData.uom,
              ship_to: currentCommercialInvoiceData.ship_to,
              document_type: currentCommercialInvoiceData.document_type || "CommercialInvoice",
              folder_name: currentCommercialInvoiceData.folder_name || "Patheon",
              site_name: currentCommercialInvoiceData.site_name || "Toronto",
              filename: currentCommercialInvoiceData.filename,
              s3_location: currentCommercialInvoiceData.s3_location,
              noOfFieldsToBeReviewed: validationCounts.noOfFieldsToBeReviewed,
              noOfFieldsReviewed: validationCounts.noOfFieldsReviewed,
            };

            // Prepare API request payload for handleDataExtractionEditedData
            const requestPayload = {
              schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
              commercialInvoiceJSONEdited: updatedCommercialInvoiceJSON,
              poNumber: poNumber,
              batchNumber: batchNumber,
              cdmoOrgID: batchDetails?.vendorMosymphonyID,
              file_type: "CommercialInvoice",
            };

            // Make API call
            const response = await axios.post(
              `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/handleDataExtractionEditedData`,
              requestPayload,
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            if (response.data.status) {
              // Save changes locally
              setLastSavedValues(formValues);
              setIsEditable(false);
              setIsShowingOriginal(false);
              
              // Refresh batch details
              const refreshData = {
                schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
              };
              dispatch(fetchDSBatchDetails(refreshData));
              
              // Trigger data refresh if callback provided
              if (onDataRefresh) {
                onDataRefresh();
              }
              
              // console.log('Commercial Invoice data saved successfully');
            } else {
              console.error('Failed to save:', response.data.message);
            }
          } catch (error) {
            console.error('Error saving Commercial Invoice data:', error);
          } finally {
            setSaving(false);
          }
        }
      };

      const isFieldChanged = (key) => {
        return getValueForField(key) !== (originalValues[key] && typeof originalValues[key] === 'object' ? originalValues[key].value : originalValues[key]);
      };

    return (
        <div>
            <Grid container direction="column" spacing={2}>
        <Grid item container alignItems="center" spacing={2}>
          <Grid item>
            <Button
              startIcon={<ChevronLeft fontSize="small" />}
              style={{
                fontWeight: "bold",
                textTransform: "none",
                marginLeft: "0px",
              }}
              onClick={onClose}
            >
              Back
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="h6">Digitized Commercial Invoice</Typography>
          </Grid>
        </Grid>

        <Grid item>
          <Divider
            style={{
              margin: "0px 0px 0px 0px",
              backgroundColor: "#707070",
              height: 1,
              zIndex: 4,
            }}
          />
        </Grid>

        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Grid item style={{display: "flex", alignItems: "center"}} spacing={2}>
              <Typography
                variant="h6"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  marginRight: "8px",
                }}
              >
                Commercial Invoice Data
              </Typography>
              <Box display="flex" gap={1} spacing={2}>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    if (isEditable) {
                      handleSave();
                    } else {
                      setIsEditable(true);
                      setIsShowingOriginal(false);
                    }
                  }}
                  disabled={saving}
                  style={{ marginRight: "8px", minWidth: 15, textAlign: "center" }}
                >
                  {isEditable ? (saving ? "Saving..." : "Save Changes") : "Edit"}
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    if (!isShowingOriginal) {
                      setFormValues(originalValues);
                    } else {
                      setFormValues(lastSavedValues);
                    }
                    setIsShowingOriginal(!isShowingOriginal);
                    setIsEditable(false);
                  }}
                  style={{ marginRight: "8px", minWidth: 20, textAlign: "center", size: "20px" }}
                >
                  {isShowingOriginal
                    ? "Show Edited Version"
                    : "Show Unedited Version"}
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">PDF Preview</Typography>
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {fields.map((field) => (
                <Grid
                  container
                  key={field.key}
                  alignItems="center"
                  spacing={2}
                  className={classes.formField}
                >
                  <Grid item xs={4}>
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      {field.label}
                      {![
                            "Sponsor SKU",
                            "Sponsor Description" ,
                            "Actual Component Quantity Consumed",
                      ].includes(field.label) && (
                        <span className={classes.requiredStar}>*</span>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={getValueForField(field.key)}
                      onChange={(e) =>
                        handleInputChange(field.key, e.target.value)
                      }
                      onBlur={(e) => {
                        const { value } = e.target;
                        // Trim leading and trailing spaces for string values
                        if (
                          typeof value === "string" &&
                          value.trim() !== value
                        ) {
                          const trimmedValue = value.trim();
                          handleInputChange(field.key, trimmedValue);
                        }
                      }}
                      placeholder={`Enter ${field.label}`}
                      error={!!validationErrors[field.key]}
                      helperText={validationErrors[field.key]}
                      InputProps={{
                        readOnly: !isEditable,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box display="flex" alignItems="center" pl={1}>
                              <Box
                                style={{
                                  width: 1,
                                  height: 39,
                                  backgroundColor: "#ccc",
                                  marginRight: 3,
                                }}
                              />
                              {!isEditable ? (
                                isFieldChanged(field.key)  ? (
                                  <Box
                                    onClick={() => {
                                      setModalData({
                                        field: field.label,
                                        before: originalValues[field.key] && typeof originalValues[field.key] === 'object' ? originalValues[field.key].value : originalValues[field.key],
                                        after: getValueForField(field.key),
                                        updatedBy: "user123",
                                        timestamp: new Date().toLocaleString(),
                                      });
                                      setOpenModal(true);
                                    }}
                                    style={{
                                      backgroundColor: "#7777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box
                                    style={{
                                      backgroundColor: getLevelColor(
                                        getConfidenceForField(field.key)
                                      ),
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                    }}
                                  >
                                    {Math.round(
                                      getConfidenceForField(field.key)
                                    )}
                                  </Box>
                                )
                              ) : (
                                <Box
                                  style={{
                                    backgroundColor: getLevelColor(
                                      getConfidenceForField(field.key)
                                    ),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}
                                >
                                  {Math.round(
                                    getConfidenceForField(field.key)
                                  )}
                                </Box>
                              )}
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              ))}

              <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
                <Typography variant="body2" color="textSecondary">
                  Confidence Levels Legend:{" "}
                </Typography>
                <Grid item style={{ width: "28px" }} />
                <Box
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 30,
                  }}
                >
                  &gt; 90
                </Box>
                <Box
                  style={{
                    backgroundColor: "orange",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 50,
                    marginLeft: 16,
                  }}
                >
                  80 to 90
                </Box>
                <Box
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 30,
                    marginLeft: 16,
                  }}
                >
                  &lt; 80
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {schrockenCustomFileID ? (
                <PDFViewer schrockenCustomFileID={schrockenCustomFileID} />
              ) : (
                <Box
                  style={{
                    height: 500,
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #ccc",
                  }}
                >
                  <Typography>PDF not available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {modalData.field} - Field Data History
          </Typography>

          <Table>
            <TableHead
              style={{ border: "1px solid grey", backgroundColor: "#7777" }}
            >
              <TableRow
                style={{ border: "1px solid grey", backgroundColor: "#7777" }}
              >
                <TableCell>
                  <b>Timestamp</b>
                </TableCell>
                <TableCell>
                  <b>Before Value</b>
                </TableCell>
                <TableCell>
                  <b>After Value</b>
                </TableCell>
                <TableCell>
                  <b>Updated By</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody style={{ border: "1px solid grey" }}>
              <TableRow>
                <TableCell>{modalData.timestamp}</TableCell>
                <TableCell>{modalData.before}</TableCell>
                <TableCell>{modalData.after}</TableCell>
                <TableCell>{modalData.updatedBy}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(false)}
            >
              Ok
            </Button>
          </Box>
        </Box>
      </Modal>
    
            
        </div>
    );
}

export default CommercialInvoice;